/**
 * NavList — shared focus-ring navigation for the M3 arcade screens
 * (GARAGE / OPTIONS / RANKINGS / PRACTICE).
 *
 * Owns one list of focusable items: wraps up/down movement, applies the
 * `is-focused` class + `aria-current` (same visual contract as the M1
 * MainMenuScreen), activates on confirm, and forwards left/right to an
 * optional per-item `adjust` hook (sliders, segmented rows).
 *
 * Deliberately DOM-light — items only need `classList.toggle`,
 * `setAttribute`/`removeAttribute` — so behavior is unit-testable in the
 * node vitest environment with fakes. Sounds are decoupled: the owner passes
 * an `onNav` callback (screens wire it to `uiSfx.play("nav")`).
 */

export interface NavElement {
  classList: { toggle(cls: string, force?: boolean): unknown };
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

export interface NavItem {
  el: NavElement;
  /** Confirm / click. */
  activate: () => void;
  /** Optional left/right handler. Return true when the input was used. */
  adjust?: (delta: 1 | -1) => boolean | void;
}

const CLS_FOCUS = "is-focused";

export class NavList {
  private items: NavItem[] = [];
  private idx = 0;

  constructor(private readonly onNav?: () => void) {}

  /** Replace the item list. Keeps the focused index clamped (not wrapped). */
  setItems(items: NavItem[], preserveIndex = true): void {
    for (const it of this.items) this.unfocus(it);
    this.items = items;
    if (!preserveIndex) this.idx = 0;
    if (this.idx >= items.length) this.idx = Math.max(0, items.length - 1);
    this.applyFocus();
  }

  length(): number {
    return this.items.length;
  }

  focusIdx(): number {
    return this.idx;
  }

  current(): NavItem | null {
    return this.items[this.idx] ?? null;
  }

  /** Move focus by delta with wrap-around. Plays onNav when focus changed. */
  move(delta: number): void {
    const n = this.items.length;
    if (n === 0) return;
    const next = (((this.idx + delta) % n) + n) % n;
    this.focusTo(next, true);
  }

  focusTo(idx: number, sfx: boolean): void {
    if (idx < 0 || idx >= this.items.length) return;
    const changed = idx !== this.idx;
    this.idx = idx;
    this.applyFocus();
    if (changed && sfx) this.onNav?.();
  }

  /** Activate (confirm) the focused item. Returns true when one existed. */
  activate(): boolean {
    const item = this.items[this.idx];
    if (!item) return false;
    item.activate();
    return true;
  }

  /** Route left/right to the focused item's adjust hook. */
  adjust(delta: 1 | -1): boolean {
    const item = this.items[this.idx];
    if (!item || !item.adjust) return false;
    return item.adjust(delta) === true;
  }

  /** Remove the visible focus ring from every item (leaving the list). */
  clearFocus(): void {
    for (const it of this.items) this.unfocus(it);
  }

  /** Re-apply the focus ring (returning to the list). */
  applyFocus(): void {
    this.items.forEach((item, i) => {
      const focused = i === this.idx;
      item.el.classList.toggle(CLS_FOCUS, focused);
      if (focused) item.el.setAttribute("aria-current", "true");
      else item.el.removeAttribute("aria-current");
    });
    const cur = this.items[this.idx];
    if (cur) {
      // Keep the focused control in view inside scrollable panes (browser only).
      (cur.el as { scrollIntoView?: (opts?: unknown) => void }).scrollIntoView?.({
        block: "nearest",
      });
    }
  }

  private unfocus(item: NavItem): void {
    item.el.classList.toggle(CLS_FOCUS, false);
    item.el.removeAttribute("aria-current");
  }
}
